/**
 * Studio Context Provider
 * 
 * Manages the global studio context (client, project, location, product scope).
 * This is the "Context System" layer that precedes workflow execution,
 * allowing workflows to be informed by selected context (e.g., products).
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Location type — a per-client scene/environment preset stored in Azure Table Storage.
 * Mirrors the backend Location interface in AzureAPI/src/types/location.ts.
 */
export interface Location {
  locationId: string;
  tenantId: string;
  clientId?: string;
  title: string;
  description: string;
  comfyText: string;
  thumbnail?: string;
  importedAt: string;
  importedBy: string;
  updatedAt: string;
}

/**
 * Product type (frontend mirror of backend Product interface)
 */
export interface Product {
  productId: string;
  tenantId: string;
  clientId: string;
  name: string;
  classification: string;
  variant: string;
  description: string;
  sku: string;
  price: string;
  currency: string;
  images: string[];
  primaryImage: string;
  sourceUrl: string;
  specifications: Record<string, string>;
  availability: string;
  importedAt: string;
  importedBy: string;
  updatedAt: string;
  tags: string[];
}

/**
 * Sheet-driven context selection.
 *
 * Represents the user's choice within a single CCStudio_SheetSelector-backed
 * sidebar section. Keyed in state by `contextId` (the same value the workflow
 * author sets on the Comfy node), so a workflow can declare any number of
 * independent context sections — Locations, Lighting, Backdrops, etc. — and
 * each gets its own slot.
 */
export interface SheetSelection {
  /** Stable identity matching the workflow node's context_id */
  contextId: string;
  /** The sheet this selection was made against */
  sheetId: string;
  /** The chosen tab name (becomes ${contextId}__tab at submit time) */
  tab: string;
  /** The chosen row's Title column value (becomes ${contextId}__row at submit time) */
  rowTitle: string;
  /** Full row payload for sidebar preview (Title, Description, Thumbnail, …) */
  row?: Record<string, string>;
}

/**
 * Studio context state
 */
export interface StudioContextState {
  /** Active client ID (e.g., "umage") */
  client: string | null;
  /** Active project name */
  project: string | null;
  /** Selected location/scene context */
  location: Location | null;
  /** Currently selected products for this session */
  selectedProducts: Product[];
  /** Sheet-driven selections, keyed by context_id */
  sheetSelections: Record<string, SheetSelection>;
}

/**
 * Studio context value (state + actions)
 */
interface StudioContextType {
  context: StudioContextState;
  /** Set the active client */
  setClient: (clientId: string | null) => void;
  /** Set the active project */
  setProject: (project: string | null) => void;
  /** Set the location/scene context */
  setLocation: (location: Location | null) => void;
  /** Set selected products (replaces current selection) */
  setSelectedProducts: (products: Product[]) => void;
  /** Add a product to the selection */
  addProduct: (product: Product) => void;
  /** Remove a product from the selection */
  removeProduct: (productId: string) => void;
  /** Clear all selected products */
  clearProducts: () => void;
  /** Set or clear the sheet selection for a given contextId (pass null to clear) */
  setSheetSelection: (contextId: string, selection: SheetSelection | null) => void;
  /** Read the current selection for a contextId, if any */
  getSheetSelection: (contextId: string) => SheetSelection | undefined;
  /** Clear all sheet-driven selections */
  clearSheetSelections: () => void;
  /** Clear entire context */
  clearContext: () => void;
  /** Check if a product is selected */
  isProductSelected: (productId: string) => boolean;
}

const initialState: StudioContextState = {
  client: null,
  project: null,
  location: null,
  selectedProducts: [],
  sheetSelections: {},
};

const StudioContext = createContext<StudioContextType | undefined>(undefined);

export const StudioContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [context, setContext] = useState<StudioContextState>(initialState);

  const setClient = useCallback((clientId: string | null) => {
    setContext(prev => ({
      ...prev,
      client: clientId,
      // Clear products when client changes (products are per-client)
      selectedProducts: clientId !== prev.client ? [] : prev.selectedProducts,
    }));
  }, []);

  const setProject = useCallback((project: string | null) => {
    setContext(prev => ({ ...prev, project }));
  }, []);

  const setLocation = useCallback((location: Location | null) => {
    setContext(prev => ({ ...prev, location }));
  }, []);

  const setSelectedProducts = useCallback((products: Product[]) => {
    setContext(prev => ({ ...prev, selectedProducts: products }));
  }, []);

  const addProduct = useCallback((product: Product) => {
    setContext(prev => {
      if (prev.selectedProducts.some(p => p.productId === product.productId)) {
        return prev; // Already selected
      }
      return { ...prev, selectedProducts: [...prev.selectedProducts, product] };
    });
  }, []);

  const removeProduct = useCallback((productId: string) => {
    setContext(prev => ({
      ...prev,
      selectedProducts: prev.selectedProducts.filter(p => p.productId !== productId),
    }));
  }, []);

  const clearProducts = useCallback(() => {
    setContext(prev => ({ ...prev, selectedProducts: [] }));
  }, []);

  const setSheetSelection = useCallback(
    (contextId: string, selection: SheetSelection | null) => {
      setContext(prev => {
        const next = { ...prev.sheetSelections };
        if (selection === null) {
          delete next[contextId];
        } else {
          next[contextId] = selection;
        }
        return { ...prev, sheetSelections: next };
      });
    },
    []
  );

  const getSheetSelection = useCallback(
    (contextId: string) => context.sheetSelections[contextId],
    [context.sheetSelections]
  );

  const clearSheetSelections = useCallback(() => {
    setContext(prev => ({ ...prev, sheetSelections: {} }));
  }, []);

  const clearContext = useCallback(() => {
    setContext(initialState);
  }, []);

  const isProductSelected = useCallback((productId: string) => {
    return context.selectedProducts.some(p => p.productId === productId);
  }, [context.selectedProducts]);

  return (
    <StudioContext.Provider
      value={{
        context,
        setClient,
        setProject,
        setLocation,
        setSelectedProducts,
        addProduct,
        removeProduct,
        clearProducts,
        setSheetSelection,
        getSheetSelection,
        clearSheetSelections,
        clearContext,
        isProductSelected,
      }}
    >
      {children}
    </StudioContext.Provider>
  );
};

export const useStudioContext = () => {
  const ctx = useContext(StudioContext);
  if (ctx === undefined) {
    throw new Error('useStudioContext must be used within a StudioContextProvider');
  }
  return ctx;
};

